import * as React from 'react';
import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import * as reactGlobalClick from '../ts/reactGlobalClick';
import { Table, ITableOptions, ITableColumn } from './tableSelect/Table';
import { IPopupOptions } from '../../../pdc-platform/src/ts/actions/DialogActions';
import { IValuesCollection } from '../ts/appTheme';
import { useElementsReorderingHook } from '../ts/DragDropCommon';
import { getSelectedItemStyle } from './selectors/SelectedItem';

interface ITableDropdownOptions extends ITableOptions, IPopupOptions {
    types: any[];

    onClose(): void;
    updateState(state): void;
}

const TABLE_COLUMNS: ITableColumn[] = [
    {
        className: 'column-select',
        title: ''
    },

    {
        className: 'column-name',
        title: 'Name',
        sort: true
    },

    {
        className: 'column-types',
        title: 'Affects',
        sort: true
    }
];

function TableDropdown(props: {
    zIndex: number;
    options: ITableDropdownOptions;

    close(): void;
}) {
    const [, setUpdateState] = React.useState({});
    const globalClickIndex = React.useRef(-1);

    React.useEffect(() => {
        props.options.updateState((obj) => {
            try {
                setUpdateState(obj);
            } catch (e) {}
        });

        globalClickIndex.current = reactGlobalClick.register((e) => {
            if (typeof props.options.onClose === 'function') {
                props.options.onClose();
            }

            props.close();
        }, 1);

        return () => {
            reactGlobalClick.unregister(globalClickIndex.current);
            globalClickIndex.current = null;
        };
    }, [props.options.updateState, props.options.onClose]);

    const handleOnContainerClick = (e) => {
        reactGlobalClick.cancel();
    };

    const getRowClickHandlers = (key, onClick) => {
        return [onClick];
    };

    const getRowContent = (key) => {
        const obj = props.options.valuesHash[key];
        const types = _.chain(obj.type)
            .map((id) => {
                const obj = props.options.types[id];

                if (obj == null) {
                    return;
                }

                return obj.title;
            })
            .compact()
            .value();

        return [types.join(', ')];
    };

    return (
        <div className="suggestions-list table-select-list" onClick={handleOnContainerClick}>
            <Table
                columns={TABLE_COLUMNS}
                options={props.options}
                getRowClickHandlers={getRowClickHandlers}
                getRowContent={getRowContent}
            />
        </div>
    );
}

export function ThemesTableSelect(props: {
    value: any[];
    valuesHash;
    values;
    displayKey?: string;
    singleSelectMode?: boolean;
    //search?;
    types?;
    theme: IValuesCollection;
    glob: IGlob;

    onChange?(value: any[]): void;
}) {
    const [dropdownVisible, setDropdownVisible] = React.useState(false);
    const updateDropdownState = React.useRef(null);
    const searchFieldRef = React.useRef<HTMLDivElement>(null);

    useElementsReorderingHook(
        searchFieldRef,
        {
            axis: 'x',
            sortableItems: '.label-value'
        },
        (start: number, stop: number) => {
            if (props.onChange != null) {
                const newValue = [...props.value];
                const item = newValue.splice(start, 1);

                newValue.splice(stop, 0, item[0]);
                props.onChange(newValue);
            }
        },
        [props.value, props.onChange]
    );

    const processOnChange = (value) => {
        if (typeof props.onChange === 'function') {
            props.onChange(value);
        }
    };

    const handleChangeFromTable = (obj) => {
        const value = objToArray(props.value, obj);

        processOnChange(value);
    };

    const handleOnClose = () => {
        setDropdownVisible(false);
    };

    const getItemRemoveHandler = (id) => {
        return (e) => {
            let newValue = [...props.value];

            newValue = _.without(newValue, id);
            processOnChange(newValue);

            if (typeof updateDropdownState.current === 'function') {
                updateDropdownState.current({
                    value: arrayToObj(newValue)
                });
            }

            e.stopPropagation();
            e.preventDefault();
        };
    };

    const dropdownStateUpdateHook = (callback) => {
        updateDropdownState.current = (obj) => {
            callback(obj);
        };
    };

    const arrayToObj = (arr: any[]) => {
        return _.reduce(
            arr,
            (res, key) => {
                res[key] = true;
                return res;
            },
            {}
        );
    };

    const objToArray = (curr, obj) => {
        const keys = _.map(_.keys(obj), (key) => parseInt(key, 10));

        curr = _.filter(curr, (key: any) => obj[key] != null);
        curr = curr.concat(_.difference(keys, curr));
        return curr;
    };

    const showDropdown = (e) => {
        reactGlobalClick.trigger(e, 1);
        reactGlobalClick.cancel();

        if (dropdownVisible) {
            return;
        }

        const component = $(searchFieldRef.current);
        const offset = component.offset();

        props.glob.DialogActions.createPopup<ITableDropdownOptions>('', {
            top: offset.top,
            left: offset.left,
            minWidth: component.outerWidth(),
            parentComponentHeight: component.outerHeight(),

            value: arrayToObj(props.value),
            values: props.values,
            valuesHash: props.valuesHash,
            displayKey: props.displayKey,
            singleSelectMode: props.singleSelectMode,
            types: props.types,

            onChange: handleChangeFromTable,
            onClose: handleOnClose,
            updateState: dropdownStateUpdateHook,

            component: TableDropdown
        });

        setDropdownVisible(true);

        e.stopPropagation();
        e.preventDefault();
    };

    const itemStyle = getSelectedItemStyle(props.theme);
    const elements = _.map(props.value, (key) => {
        let display;

        if (props.valuesHash != null && props.valuesHash[key] != null) {
            display = props.valuesHash[key][props.displayKey];
        } else {
            display = key;
        }

        let remove;

        if (props.singleSelectMode !== true) {
            remove = <span className="glyphicon glyphicon-remove" onClick={getItemRemoveHandler(key)}></span>;
        }

        return (
            <li key={key} className="pull-left label-value" style={itemStyle}>
                <span>{display}</span>
                {remove}
            </li>
        );
    });

    let content: React.JSX.Element;

    if (elements.length === 0) {
        content = <em className="empty"></em>;
    } else {
        content = <ul className="list-unstyled item-selector-values-list">{elements}</ul>;
    }

    return (
        <div className="suggestion-select table-select" onClick={showDropdown}>
            <div ref={searchFieldRef} className="search-field-group form-control">
                {content}

                <div className="dropdown-toggle-button">
                    <span className="glyphicon glyphicon-chevron-down"></span>
                </div>
            </div>
        </div>
    );
}
